import React, { useEffect, useState } from 'react';
import { Box, Button, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { InlineMessage, SectionCard, StatusChip } from '../../ui';
import { useToast } from '../../../contexts/ToastContext';
import { useAppTranslation } from '../../../i18n/useAppTranslation';
import { tokens } from '../../../theme/tokens';
import {
  LENS_FOCAL_CLASSES,
  LENS_KINDS,
  lensFocalRangeApi,
  LensFocalClass,
  LensFocalRange,
  LensKind,
} from '../../../services/lensFocalRangeApi';

const queryKey = (productId: string) => ['admin-lens-focal-range', productId];

/**
 * Admin product detail, Specs tab, lenses only (MOT-452): the lens's focal-range class, where
 * it came from, and a hand-set value that outranks every other source. Saves on its own call.
 */
export const LensFocalRangePanel: React.FC<{ productId: string }> = ({ productId }) => {
  const { t } = useAppTranslation();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const view = useQuery({ queryKey: queryKey(productId), queryFn: () => lensFocalRangeApi.getProduct(productId), retry: false });
  const [focalClass, setFocalClass] = useState<LensFocalClass | ''>('');
  const [lensKind, setLensKind] = useState<LensKind | ''>('');

  useEffect(() => {
    const effective = view.data?.effective;
    setFocalClass(effective?.focalClass ?? '');
    setLensKind(effective?.lensKind ?? '');
  }, [view.data]);

  const onDone = (message: string) => (data: Awaited<ReturnType<typeof lensFocalRangeApi.getProduct>>) => {
    queryClient.setQueryData(queryKey(productId), data);
    showToast(message, 'success');
  };
  const onError = (error: unknown) => showToast(error instanceof Error ? error.message : String(error), 'error');
  const save = useMutation({
    mutationFn: () => lensFocalRangeApi.setProduct(productId, { focalClass: focalClass as LensFocalClass, lensKind: lensKind || null }),
    onSuccess: onDone(t('lensFocalRange.product.saved')),
    onError,
  });
  const clear = useMutation({
    mutationFn: () => lensFocalRangeApi.clearProduct(productId),
    onSuccess: onDone(t('lensFocalRange.product.cleared')),
    onError,
  });

  const describe = (range: LensFocalRange | null): string => {
    if (!range) return t('lensFocalRange.none');
    const kind = range.lensKind ? t(`lensFocalRange.kinds.${range.lensKind}`) : t('lensFocalRange.kinds.unknown');
    return `${kind} · ${t(`lensFocalRange.classes.${range.focalClass}`)}`;
  };
  const detail = (range: LensFocalRange): string => {
    if (range.source === 'computed') {
      const own = range.focalMinMm !== undefined
        ? range.focalMinMm === range.focalMaxMm ? `${range.focalMinMm}mm` : `${range.focalMinMm}-${range.focalMaxMm}mm`
        : '';
      const eq = range.equivalentMinMm === range.equivalentMaxMm ? `${range.equivalentMinMm}mm` : `${range.equivalentMinMm}-${range.equivalentMaxMm}mm`;
      return t('lensFocalRange.product.computedDetail', {
        own: own || '—',
        equivalent: eq,
        basis: t(`lensFocalRange.basis.${range.basis || 'spec'}`),
        crop: t(`lensFocalRange.crop.${range.cropBasis || 'assumed_full_frame'}`, { factor: range.cropFactor ?? 1 }),
      });
    }
    return range.label ? t('lensFocalRange.product.labelDetail', { label: range.label }) : '';
  };

  if (view.isLoading) return null;
  if (view.isError) {
    return (
      <InlineMessage severity="warning">
        {t('lensFocalRange.product.loadFailed')}{' '}
        <Button size="small" onClick={() => view.refetch()}>{t('lensFocalRange.product.retry')}</Button>
      </InlineMessage>
    );
  }
  const data = view.data;
  if (!data?.isLens) return null;
  const effective = data.effective;
  const isAdmin = data.stored?.source === 'admin';
  const unchanged = effective && focalClass === effective.focalClass && (lensKind || null) === effective.lensKind && isAdmin;
  const primeSuperzoom = lensKind === 'prime' && focalClass === 'superzoom';

  return (
    <SectionCard dense padded title={t('lensFocalRange.product.title')} subtitle={t('lensFocalRange.product.description')}>
      <Stack spacing={1.5}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" sx={{ color: tokens.ink.primary, fontWeight: 600 }}>{describe(effective)}</Typography>
          {effective && <StatusChip tone={isAdmin ? 'info' : 'neutral'} label={t(`lensFocalRange.sources.${effective.source}`)} />}
          {data.categoryKey && (
            <Typography variant="caption" sx={{ color: tokens.ink.muted }}>
              {t('lensFocalRange.product.mapKey', { key: data.categoryKey })}
            </Typography>
          )}
        </Box>
        {effective && detail(effective) && (
          <Typography variant="caption" sx={{ color: tokens.ink.muted }}>{detail(effective)}</Typography>
        )}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25, alignItems: 'center' }}>
          <TextField
            select
            size="small"
            label={t('lensFocalRange.product.classLabel')}
            value={focalClass}
            onChange={(e) => setFocalClass(e.target.value as LensFocalClass)}
            sx={{ minWidth: 160 }}
          >
            {LENS_FOCAL_CLASSES.map((key) => (
              <MenuItem key={key} value={key}>{t(`lensFocalRange.classes.${key}`)}</MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label={t('lensFocalRange.product.kindLabel')}
            value={lensKind}
            onChange={(e) => setLensKind(e.target.value as LensKind | '')}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="">{t('lensFocalRange.kinds.unknown')}</MenuItem>
            {LENS_KINDS.map((key) => (
              <MenuItem key={key} value={key}>{t(`lensFocalRange.kinds.${key}`)}</MenuItem>
            ))}
          </TextField>
          <Button
            size="small"
            variant="contained"
            disabled={!focalClass || primeSuperzoom || Boolean(unchanged) || save.isPending}
            title={primeSuperzoom ? t('lensFocalRange.product.primeSuperzoom') : undefined}
            onClick={() => save.mutate()}
          >
            {t('lensFocalRange.product.setByHand')}
          </Button>
          {isAdmin && (
            <Button size="small" disabled={clear.isPending} onClick={() => clear.mutate()}>
              {t('lensFocalRange.product.clear')}
            </Button>
          )}
        </Box>
        {primeSuperzoom && (
          <Typography variant="caption" sx={{ color: tokens.signal.caution.ink }}>{t('lensFocalRange.product.primeSuperzoom')}</Typography>
        )}
      </Stack>
    </SectionCard>
  );
};
